import { ExternalLink } from '@/components/common/external-link';
import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';
import { Button } from '@heidi/design-system/button';
import { dictationIntlMessages, type ErrorCode, type MessageLevel } from '@heidi/dictate';
import { AlertTriangle, ExternalLinkIcon, Info } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface DictationErrorContentProps {
  errorCode: ErrorCode | null;
  messageLevel: MessageLevel | null;
  /** Optional close button (positioned absolutely by the consumer). */
  closeButton?: ReactNode;
  /** Whether to show the help link for mic-related errors. @default false */
  showHelpLink?: boolean;
}

const HELP_LINK_URL =
  'articles/11016383-troubleshooting-microphone-and-audio-issues-in-heidi';

function DictationErrorMessage({ errorCode }: { errorCode: ErrorCode | null }) {
  switch (errorCode) {
    case 'MIC_PERMISSION_DENIED':
      return <FormattedMessage {...dictationIntlMessages.errorMicPermissionDenied} />;
    case 'NO_MIC_DEVICES':
      return <FormattedMessage {...dictationIntlMessages.errorNoMicDevices} />;
    case 'FAILED_TO_GET_AUDIO_STREAM':
      return <FormattedMessage {...dictationIntlMessages.errorFailedToGetAudioStream} />;
    case 'PRESS_AND_HOLD':
      return <FormattedMessage {...dictationIntlMessages.errorPressAndHold} />;
    case 'COULD_NOT_HEAR_LONG':
      return <FormattedMessage {...dictationIntlMessages.errorNoAudioDetected} />;
    case 'NO_WORDS_DETECTED':
      return <FormattedMessage {...dictationIntlMessages.errorNoWordsDetected} />;
    case 'CANCELLED':
      return <FormattedMessage {...dictationIntlMessages.errorCancelled} />;
    case 'COULD_NOT_CONNECT_TO_HEIDI':
      return <FormattedMessage {...dictationIntlMessages.errorCouldNotConnectToHeidi} />;
    case 'AUTHENTICATION_REQUIRED':
      return <FormattedMessage {...dictationIntlMessages.errorAuthenticationRequired} />;
    case 'CHUNK_RECORDER_ERROR':
      return <FormattedMessage {...dictationIntlMessages.errorChunkRecorderError} />;
    case 'NO_INPUT_FIELD_SELECTED':
      return <FormattedMessage {...dictationIntlMessages.errorNoInputFieldSelected} />;
    default:
      return <FormattedMessage {...dictationIntlMessages.errorSomethingWentWrong} />;
  }
}

function useErrorIcon(messageLevel: MessageLevel | null) {
  return useMemo(() => {
    switch (messageLevel) {
      case 'INFO':
        return <Info size={20} className="shrink-0 text-text-secondary" />;
      case 'WARN':
        return <AlertTriangle size={20} className="shrink-0 text-validation-warning" />;
      case 'ERROR':
        return (
          <AlertTriangle size={20} className="shrink-0 text-validation-destructive" />
        );
      default:
        return null;
    }
  }, [messageLevel]);
}

function useErrorTextColor(messageLevel: MessageLevel | null) {
  return useMemo(() => {
    switch (messageLevel) {
      case 'INFO':
        return 'text-text-secondary';
      case 'WARN':
        return 'text-validation-warning';
      case 'ERROR':
        return 'text-validation-destructive';
      default:
        return 'text-text-tertiary';
    }
  }, [messageLevel]);
}

export const DictationErrorContent = ({
  errorCode,
  messageLevel,
  closeButton,
  showHelpLink = false,
}: DictationErrorContentProps) => {
  const intl = useIntl();
  const icon = useErrorIcon(messageLevel);
  const textColor = useErrorTextColor(messageLevel);

  const shouldShowHelpLink =
    showHelpLink &&
    (errorCode === 'MIC_PERMISSION_DENIED' || errorCode === 'NO_MIC_DEVICES');

  return (
    <div className={`flex items-center gap-4 ${textColor}`}>
      {closeButton}
      {icon}
      <span className="shrink-0">
        <DictationErrorMessage errorCode={errorCode} />
      </span>
      {shouldShowHelpLink && (
        <Button asChild size="sm">
          <ExternalLink href={getHelpCenterUrl(HELP_LINK_URL, intl.locale)}>
            <FormattedMessage {...dictationIntlMessages.helpLink} />
            <ExternalLinkIcon />
          </ExternalLink>
        </Button>
      )}
    </div>
  );
};
